import {faTools} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  ActionIcon,
  Button,
  Divider,
  Group,
  Paper,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {FC, useCallback, useMemo, useState} from 'react';
import {SubmitHandler} from 'react-hook-form';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {JobStatusTypes, JobTypes, Roles} from '../../../api/generated/enums';
import {Error} from '../../../api/generated/index.defs';
import {JobsService} from '../../../api/generated/JobsService';
import {ProtectedElement} from '../../../auth/require-authorization';
import {AsyncStateContainer} from '../../../components/async-state-container';
import {createForm} from '../../../forms';
import {useNotification} from '../../../hooks/use-notifications';
import {ROUTES} from '../../../routes';
import {useJobStore} from '../../job-store';
import classes from '../job-evaluation.module.css';
import {MmJobPartListRows, NonMmJobPartListRows} from './part-item-rows';
import {usePartsListStore} from './parts-list-store';
import {JobPartsListSchema, jobPartsListSchema, nonMmJobPartsListSchema} from './schema';
import {useAsyncFn} from 'react-use';

export const EditPartItemRoles = ['Global Admin'] as Roles[];

export const PartsListForm = createForm<JobPartsListSchema>();

type PartsListProps = {
  children?: React.ReactNode;
};

export const PartsList: React.FC<PartsListProps> = () => {
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);

  const [fieldErrors, setFieldErrors] = useState<Error[]>([]);

  const {job, isLoading, isJobReadonly, fetchJob, fetchReplacedParts} =
    useJobStore((state) => ({
      job: state.job,
      isLoading: state.isJobLoading,
      isJobReadonly: state.isJobReadonly,
      fetchJob: state.fetchJob,
      fetchReplacedParts: state.fetchReplacedParts,
    }));

  const {partsWithActionQty, showAdminFeatures, toggleAdminFeatures} =
    usePartsListStore((state) => ({
      partsWithActionQty: state.partsWithActionQty,
      showAdminFeatures: state.showAdminFeatures,
      toggleAdminFeatures: state.toggleAdminFeatures,
    }));

  const defaultValues = useMemo(
    () => ({partItems: job?.partItems || []}),
    [job?.partItems],
  );

  const onSubmit = useCallback<SubmitHandler<JobPartsListSchema>>(
    async (value) => {
      setFieldErrors([]);
      const response = await JobsService.updatePartsList({id, body: value});

      if (response.hasErrors && response.errors) {
        setFieldErrors(response.errors);
        return response;
      }

      notifications.success('Parts list updated');
      await fetchReplacedParts(id);
      await fetchJob(id);
    },
    [fetchJob, fetchReplacedParts, id, notifications],
  );

  const [{loading: onUpdateRevisionLoading}, onUpdateRevision] =
    useAsyncFn(async () => {
      const response = await JobsService.updateToLatestRevision({jobId: id});
      if (response.hasErrors) {
        return response;
      }
      notifications.success('Updated to latest revision');
      await fetchReplacedParts(id);
      await fetchJob(id);
    }, [fetchJob, fetchReplacedParts, id, notifications]);

  const schema = useSchema((job?.jobType ?? JobTypes['M&M']) as JobTypes);

  return (
    <div>
      <Title fz={14} className={classes.title}>
        <Group justify='space-between'>
          <Group justify='space-between' gap={9}>
            <Text size='sm'>Parts List</Text>
            {!!job?.revisionNumber && (
              <>
                <Divider color='primary' orientation='vertical' />
                <Text size='sm'>Revision: {job?.revisionNumber}</Text>
              </>
            )}

            {job?.jobType === JobTypes['M&M'] && (
              <ProtectedElement role={EditPartItemRoles}>
                <Tooltip openDelay={600} label='Edit Parts List'>
                  <ActionIcon variant='subtle' onClick={toggleAdminFeatures}>
                    <FontAwesomeIcon
                      icon={faTools}
                      className={
                        !showAdminFeatures
                          ? classes.showAdminFeaturesIconInactive
                          : ''
                      }
                    />
                  </ActionIcon>
                </Tooltip>
              </ProtectedElement>
            )}

            {job?.jobType === JobTypes['M&M'] &&
              (job.isNewRevisionAvailable || !job.revisionNumber?.startsWith('W')) &&
              (job.status === JobStatusTypes.Evaluation || job.status === JobStatusTypes.Archiving) &&
              (
                <Button
                  variant='outline'
                  onClick={onUpdateRevision}
                  loading={onUpdateRevisionLoading}
                >
                  Update to Latest Revision
                </Button>
              )}
          </Group>
        </Group>
      </Title>
      <Paper pb={16} shadow='sm' radius='0 0 6px 6px'>
        <AsyncStateContainer loading={isLoading}>
          <PartsListForm
            onSubmit={onSubmit}
            schema={schema}
            defaultValues={defaultValues}
            className={classes.tableContainer}
          >
            {({formState: {isSubmitting}}) => (
              <>
                {job && job.jobType === JobTypes['M&M'] && (
                  <MmJobPartsTable
                    showAdminFeatures={showAdminFeatures}
                    partsWithActionQty={partsWithActionQty}
                    fieldErrors={fieldErrors}
                    isJobReadonly={isJobReadonly}
                    partKitNumber={job.partKitNumber}
                  />
                )}
                {job && job.jobType === JobTypes['Non-M&M'] && (
                  <NonMmJobPartsTable
                    showAdminFeatures={showAdminFeatures}
                    partsWithActionQty={partsWithActionQty}
                    fieldErrors={fieldErrors}
                    isJobReadonly={isJobReadonly}
                    partKitNumber={job.partKitNumber}
                  />
                )}
                <Divider />
                <Group pt={16} px={16} justify='flex-end'>
                  <PartsListForm.Submit
                    disabled={isJobReadonly}
                    loading={isSubmitting}
                  >
                    Save Parts List
                  </PartsListForm.Submit>
                </Group>
              </>
            )}
          </PartsListForm>
        </AsyncStateContainer>
      </Paper>
    </div>
  );
};

const useSchema = (jobType: JobTypes) => {
  return jobType === JobTypes['Non-M&M']
    ? nonMmJobPartsListSchema
    : jobPartsListSchema;
};

type MmJobPartTableProps = {
  showAdminFeatures: boolean;
  partsWithActionQty: number[];
  fieldErrors: Error[];
  isJobReadonly: boolean;
  partKitNumber?: string;
};
const MmJobPartsTable: FC<MmJobPartTableProps> = (props) => {
  const {
    showAdminFeatures,
    fieldErrors,
    isJobReadonly,
    partsWithActionQty,
    partKitNumber,
  } = props;

  return (
    <Table withColumnBorders>
      <Table.Thead className={classes.tableHeader}>
        <Table.Tr>
          <Table.Th fw="normal">Part Number</Table.Th>
          <Table.Th fw="normal">Short Description</Table.Th>
          <Table.Th fw="normal">Description</Table.Th>
          <Table.Th fw="normal">Class</Table.Th>
          <Table.Th fw="normal">Seq</Table.Th>
          <Table.Th fw="normal">Qty</Table.Th>
          <Table.Th fw="normal">Action</Table.Th>
          {partsWithActionQty.length > 0 && (
            <Table.Th fw="normal">Action Qty</Table.Th>
          )}
          <Table.Th fw="normal">Disposition</Table.Th>
          <ProtectedElement role={EditPartItemRoles}>
            {showAdminFeatures && <Table.Th></Table.Th>}
          </ProtectedElement>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        <MmJobPartListRows
          fieldErrors={fieldErrors}
          isReadonly={isJobReadonly}
          partKitNumber={partKitNumber}
        />
      </Table.Tbody>
    </Table>
  );
};

const NonMmJobPartsTable: FC<MmJobPartTableProps> = (props) => {
  const {
    fieldErrors,
    isJobReadonly,
    partKitNumber,
  } = props;

  return (
    <Table withColumnBorders>
      <Table.Thead className={classes.tableHeader}>
        <Table.Tr>
          <Table.Th fw='normal'>OEM Part Number</Table.Th>
          <Table.Th fw='normal'>Name/Description</Table.Th>
          <Table.Th fw='normal'>OEM Lot #</Table.Th>
          <Table.Th fw='normal'>Qty</Table.Th>
          <Table.Th fw='normal'>Action</Table.Th>
          <Table.Th fw='normal'>Disposition</Table.Th>
          {!isJobReadonly && <Table.Th></Table.Th>}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        <NonMmJobPartListRows
          fieldErrors={fieldErrors}
          isReadonly={isJobReadonly}
          partKitNumber={partKitNumber}
        />
      </Table.Tbody>
    </Table>
  );
};